import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect, useState } from 'react';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { U2000TableHandlers } from '../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU3202Context } from '../U3202Context';
import { doDeleteProvider, doSearchProviders, luHandleAppiError, type U3202OrgProviderConfigSearchRow } from '../U3202Service';
import { NS } from '../U3202';
import U3202OrgProviderConfigDeleteDlg from './common/U3202OrgProviderConfigDeleteDlg';
import { U3202OrgProviderConfigListToolBar } from './list/U3202OrgProviderConfigListToolBar';
import U3202OrgProviderConfigListEmpty from './list/U3202OrgProviderConfigListEmpty';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

function parseBooleanGridValue(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'name',
      label: t('lblProvider', { ns: NS }),
      widthClass: 'w-[220px]',
      type: 'string',
      align: 'left',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, _value, row) => {
        const r = row as U3202OrgProviderConfigSearchRow;
        if (!r?.name) return null;
        return `${r.name} (${r.code ?? ''})`;
      },
    },
    {
      name: 'apiUrl',
      label: t('lblApiUrl', { ns: NS }),
      widthClass: 'w-[240px]',
      type: 'string',
      align: 'left',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'showThinking',
      label: t('showThinking', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => {
        const b = parseBooleanGridValue(value);
        if (b === true) return t('lblActiveYes', { ns: NS, defaultValue: 'Oui' });
        if (b === false) return t('lblActiveNo', { ns: NS, defaultValue: 'Non' });
        return null;
      },
    },
    {
      name: 'activatedModelCount',
      label: t('activatedModels.title', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'number',
      align: 'center',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
  ];
}

const U3202OrgProviderConfigList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3202Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);

  useEffect(() => {
    const listTitle = t('configList.title', { ns: NS });
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers instables
  }, [t]);

  const handleSearch = useU2000SearchHandlers<U3202OrgProviderConfigSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearchProviders(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3202OrgProviderConfigSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
      setHasSearchCompleted(true);
    },
    onError: ({ error }: { error: unknown }) => {
      setHasSearchCompleted(true);
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteProvider(u2000AxiosAuthenticated, id, { id: row.id, version: row.version });
    },
    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(
          t('delete.success', { provider: row.name ?? row.id, ns: NS }),
          t('successTitle', { ns: NS }),
        );
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const handlerActions: U2000TableHandlers<U3202OrgProviderConfigSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3202OrgProviderConfigSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom('/U3202');
      navigate('new');
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U3202');
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
  };

  const isGuidedEmptyState =
    hasSearchCompleted &&
    gridStateHandlers.getTotalRows() === 0 &&
    searchStateHandlers.getCriterias().length === 0;

  const handleAddConfig = () => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
    luStateHandlers.setActionOriginFrom('/U3202');
    navigate('new');
  };

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U3202OrgProviderConfigDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable<U3202OrgProviderConfigSearchRow>
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          <U3202OrgProviderConfigListToolBar
            gridStateHandlers={gridStateHandlers}
            handlerActions={handlerActions}
          />
        }
        handlerActions={handlerActions}
        label={t('lblConfigList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
        emptyTableBody={
          isGuidedEmptyState ? (
            <U3202OrgProviderConfigListEmpty onAddConfig={handleAddConfig} />
          ) : undefined
        }
      />
    </>
  );
};

export default U3202OrgProviderConfigList;
